import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useHomepagePageUserData } from 'src/app/pages/homepage/pageContext';
import type { HomepageDraftInput, HomepageDraftType } from 'src/app/pages/homepage/shared/api';
import { createDraft } from 'src/app/pages/homepage/shared/api';

type ExperimentsData = ReturnType<typeof useHomepagePageUserData>['data']['experiments'];

/**
 * Determines the draft type based on the starfishSap3RecommendedAngles experiment variant.
 * - 'variant': returns 'recommendations_draft' to enable the recommendations flow
 * - 'control' or not enrolled: returns 'ai_draft' for the default AI draft flow
 */
function getDraftTypeFromExperiment(experiments: ExperimentsData): HomepageDraftType {
	const { variation } = experiments.starfishSap3RecommendedAngles;
	return variation.includes('control') ? 'ai_draft' : 'recommendations_draft';
}

export function useCreateDraft() {
	const navigate = useNavigate();
	const track = useTracking();
	const queryClient = useQueryClient();
	const {
		data: { experiments },
	} = useHomepagePageUserData();

	const draftType = getDraftTypeFromExperiment(experiments);

	return useMutation({
		mutationFn: async (draftInput: string) => {
			const draft: HomepageDraftInput = { input: { issuePrompt: draftInput }, type: draftType };
			return createDraft(draft, { queryClient });
		},
		onSuccess: () => {
			navigate('/start-a-petition/personal-story?started_flow=false&source_location=homepage_primary_button');
		},
		onError: () => {
			void track('homepage_sap_draft_submission_error');
		},
	});
}
